import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, FileCheck, Users, Gift, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { checkAndSaveReferral, getReferralData } from "@/utils/referralTracking";
import PartnerDiscountPopup from "@/components/PartnerDiscountPopup";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [referralInfo, setReferralInfo] = useState(null);

  // Check for referral code on page load
  useEffect(() => {
    const initReferral = async () => {
      const result = await checkAndSaveReferral(API_URL);
      if (result) {
        setReferralInfo(result);
      } else {
        // Check if there's existing referral data
        const existingData = getReferralData();
        if (existingData) {
          setReferralInfo(existingData);
        }
      }
    };
    initReferral();
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      {/* Partner Discount Popup */}
      {referralInfo && (
        <PartnerDiscountPopup
          referralCode={referralInfo.code}
          affiliateName={referralInfo.affiliate_name}
        />
      )}
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-6" data-testid="hero-label">
              Duidelijkheid voor zzp'ers
            </p>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-zinc-900 mb-6" data-testid="hero-title">
              Weet jij of je{" "}
              <span className="font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] bg-clip-text text-transparent">veilig werkt?</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-zinc-600 mb-6 max-w-2xl" data-testid="hero-description">
              De online en offline wereld zorgt voor veel onduidelijkheid over schijnzelfstandigheid. 
              Meer dan <span className="font-semibold text-zinc-800">75% van de zzp'ers in Nederland</span> loopt 
              risico zonder het te weten.
            </p>
            <p className="text-base leading-relaxed text-zinc-500 mb-10 max-w-2xl">
              Proofix geeft je snel en simpel inzicht in jouw situatie. En er komt nog meer aan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/dba-check" data-testid="hero-cta-link">
                <Button 
                  className="rounded-full px-8 py-6 text-base font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white btn-hover shadow-lg shadow-blue-500/20"
                  data-testid="hero-cta-btn"
                >
                  Ontdek de DBA Check
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Animated Protection Section */}
      <section className="py-20 md:py-32 px-6 md:px-16 lg:px-24 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
              <Shield className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-medium">Waarom de DBA Check?</span>
            </div>
            <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white mb-4">
              Proofix beschermt je tegen DBA risico's
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Veel zzp'ers lopen onbewust risico. Wij helpen je dit te voorkomen.
            </p>
          </motion.div>

          {/* Animated Shield Section */}
          <div className="relative min-h-[500px] flex items-center justify-center">
            
            {/* Background glow effects */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute w-[400px] h-[400px] md:w-[600px] md:h-[600px] rounded-full bg-blue-500/10 blur-3xl"
              />
              <motion.div
                animate={{ 
                  scale: [1.2, 1, 1.2],
                  opacity: [0.2, 0.4, 0.2]
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute w-[300px] h-[300px] md:w-[500px] md:h-[500px] rounded-full bg-purple-500/10 blur-3xl"
              />
            </div>

            {/* Floating risk elements that bounce off shield */}
            {[
              { label: "Naheffingen", color: "red", x: -280, y: -120, delay: 0 },
              { label: "Boetes", color: "red", x: 290, y: -80, delay: 0.5 },
              { label: "Correcties", color: "amber", x: -260, y: 80, delay: 1 },
              { label: "Schijnzelfstandigheid", color: "red", x: 250, y: 100, delay: 1.5 },
              { label: "Juridische kosten", color: "amber", x: -200, y: -20, delay: 2 },
              { label: "DBA risico's", color: "red", x: 220, y: 10, delay: 2.5 },
            ].map((risk, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: risk.x * 1.5, y: risk.y * 1.5 }}
                whileInView={{ 
                  opacity: [0, 1, 1, 0.5],
                  x: [risk.x * 1.5, risk.x, risk.x * 0.7, risk.x * 1.2],
                  y: [risk.y * 1.5, risk.y, risk.y * 0.7, risk.y * 1.2],
                }}
                viewport={{ once: true }}
                transition={{ 
                  duration: 3, 
                  delay: risk.delay,
                  repeat: Infinity,
                  repeatDelay: 2
                }}
                className={`absolute hidden md:flex items-center gap-2 px-4 py-2 rounded-full border backdrop-blur-sm text-sm font-medium
                  ${risk.color === 'red' 
                    ? 'bg-red-500/10 border-red-500/30 text-red-400' 
                    : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                  }`}
                style={{ left: '50%', top: '50%', marginLeft: risk.x, marginTop: risk.y }}
              >
                <span className={`w-2 h-2 rounded-full ${risk.color === 'red' ? 'bg-red-400' : 'bg-amber-400'}`} />
                {risk.label}
              </motion.div>
            ))}

            {/* Central Shield */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
              className="relative z-10"
            >
              {/* Shield container */}
              <div className="relative flex items-center justify-center">
                {/* Pulsing rings - centered around shield */}
                <motion.div
                  animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute w-48 h-48 md:w-64 md:h-64 rounded-full border-2 border-blue-400/30"
                />
                <motion.div
                  animate={{ scale: [1, 1.6, 1], opacity: [0.3, 0, 0.3] }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: 0.5 }}
                  className="absolute w-48 h-48 md:w-64 md:h-64 rounded-full border-2 border-blue-400/20"
                />
                <motion.div
                  animate={{ scale: [1, 1.8, 1], opacity: [0.2, 0, 0.2] }}
                  transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                  className="absolute w-48 h-48 md:w-64 md:h-64 rounded-full border border-blue-400/10"
                />

                {/* Main shield */}
                <motion.div
                  animate={{ 
                    boxShadow: [
                      "0 0 40px rgba(59, 130, 246, 0.3)",
                      "0 0 80px rgba(59, 130, 246, 0.5)",
                      "0 0 40px rgba(59, 130, 246, 0.3)"
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="relative w-40 h-40 md:w-56 md:h-56 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-2xl"
                >
                  <div className="text-center">
                    <Shield className="w-16 h-16 md:w-24 md:h-24 text-white mb-2 mx-auto" strokeWidth={1.5} />
                    <p className="text-white font-bold text-xl md:text-2xl">Proofix</p>
                  </div>
                </motion.div>

                {/* Checkmark badge */}
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1, type: "spring", bounce: 0.5 }}
                  className="absolute -bottom-2 -right-2 md:bottom-0 md:right-0 w-14 h-14 md:w-16 md:h-16 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg"
                >
                  <Check className="w-8 h-8 md:w-10 md:h-10 text-white" strokeWidth={3} />
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Key points */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-6 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-amber-400" />
              </div>
              <h4 className="text-white font-semibold text-lg mb-2">Identificeer risico's</h4>
              <p className="text-slate-400">
                Ontdek verborgen gevaren in je huidige werkrelatie voordat het te laat is.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-6 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                <FileCheck className="w-6 h-6 text-blue-400" />
              </div>
              <h4 className="text-white font-semibold text-lg mb-2">Krijg duidelijkheid</h4>
              <p className="text-slate-400">
                Binnen minuten weet je waar je staat. Zonder juridisch jargon.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-6 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-emerald-400" />
              </div>
              <h4 className="text-white font-semibold text-lg mb-2">Voorkom problemen</h4>
              <p className="text-slate-400">
                Bescherm jezelf tegen naheffingen, boetes en juridische kosten.
              </p>
            </motion.div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center mt-12"
          >
            <Link to="/dba-check">
              <Button 
                className="rounded-full px-10 py-7 text-lg font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] hover:from-[#1a91da] hover:to-[#1d4ed8] text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all hover:-translate-y-0.5"
                data-testid="protection-cta-btn"
              >
                Start de DBA Check
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="text-sm text-slate-500 mt-4">
              Gratis risicocheck • Resultaat binnen 5 minuten
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partner Promotion Section */}
      <section className="py-12 md:py-16 px-6 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 md:p-12"
          >
            {/* Background decorations */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl" />
            
            <div className="relative grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left: Content */}
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
                  <Gift className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-400 font-medium">Partner Programma</span>
                </div>
                
                <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl font-medium text-white mb-4">
                  Verdien tot <span className="bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">€500</span> met één simpele link
                </h2>
                
                <p className="text-lg text-slate-300 mb-6">
                  Deel je link, laat ondernemers de check doen en ontvang beloningen bij elke milestone
                </p>
                
                {/* Bullet points */}
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-slate-300">Deel je unieke link</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-slate-300">Verdien cadeaubonnen bij elke milestone</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-slate-300">Je verdient alleen wanneer iemand betaalt</span>
                  </li>
                </ul>
                
                {/* Highlight */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-6">
                  <p className="text-blue-300 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-400" />
                    <span>Al vanaf 5 referrals verdien je <strong className="text-white">€15 Bol.com cadeaukaart</strong></span>
                  </p>
                </div>
                
                {/* CTA */}
                <div className="space-y-3">
                  <Link to="/partner" data-testid="partner-promo-cta">
                    <Button className="rounded-full px-8 py-6 text-base font-semibold bg-gradient-to-r from-emerald-500 to-blue-500 hover:from-emerald-600 hover:to-blue-600 text-white shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all hover:-translate-y-0.5">
                      Verdien je eerste €15
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <p className="text-sm text-slate-500">
                    Gratis • Direct starten • Geen verplichtingen
                  </p>
                </div>
              </div>
              
              {/* Right: Visual */}
              <div className="hidden md:block">
                <div className="relative">
                  {/* Milestone cards */}
                  <div className="space-y-4">
                    {[
                      { referrals: 5, reward: "€15", color: "emerald", label: "Eerste beloning" },
                      { referrals: 10, reward: "€35", color: "blue" },
                      { referrals: 25, reward: "€100", color: "purple" },
                      { referrals: 50, reward: "€250", color: "amber" },
                      { referrals: 100, reward: "€500", color: "rose" },
                    ].map((milestone, index) => (
                      <motion.div
                        key={milestone.referrals}
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 0.1 * index }}
                        className={`relative flex items-center justify-between p-4 rounded-xl border transition-all ${
                          index === 0 
                            ? "bg-gradient-to-r from-emerald-500/20 to-blue-500/20 border-emerald-500/30 shadow-lg shadow-emerald-500/10" 
                            : "bg-slate-800/50 border-slate-700/50"
                        }`}
                      >
                        {/* First milestone label */}
                        {milestone.label && (
                          <div className="absolute -top-2 left-4 px-2 py-0.5 bg-emerald-500 text-white text-xs font-semibold rounded">
                            {milestone.label}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                            index === 0 ? "bg-emerald-500/30 text-emerald-400" : "bg-slate-700 text-slate-400"
                          }`}>
                            {milestone.referrals}
                          </div>
                          <span className={index === 0 ? "text-white" : "text-slate-400"}>
                            referrals
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Gift className={`w-4 h-4 ${index === 0 ? "text-emerald-400" : "text-slate-500"}`} />
                          <span className={`font-semibold ${index === 0 ? "text-white text-lg" : "text-slate-400"}`}>
                            {milestone.reward}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 px-6 md:px-16 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-zinc-500 mb-4">
              Onze tools
            </p>
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-zinc-900">
              Producten van Proofix
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {/* DBA Check Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Link to="/dba-check" className="block" data-testid="product-card-dba">
                <div className="bg-white border border-zinc-200/60 shadow-sm rounded-2xl p-8 md:p-10 card-hover h-full">
                  <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mb-6">
                    <Shield className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="font-outfit text-xl sm:text-2xl font-medium text-zinc-900 mb-3">
                    DBA Check
                  </h3>
                  <p className="text-zinc-600 leading-relaxed mb-4">
                    Schat uw risico op schijnzelfstandigheid in met onze 
                    uitgebreide vragenlijst. Binnen 5 minuten weet u waar u staat.
                  </p>
                  <span className="inline-flex items-center text-blue-600 font-medium">
                    Start de check
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </Link>
            </motion.div>

            {/* Coming Soon Card 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white border border-zinc-200/60 shadow-sm rounded-2xl p-8 md:p-10 h-full opacity-60" data-testid="product-card-contract">
                <div className="w-14 h-14 rounded-xl bg-zinc-100 flex items-center justify-center mb-6">
                  <FileCheck className="w-7 h-7 text-zinc-400" />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-outfit text-xl sm:text-2xl font-medium text-zinc-900">
                    Contract Check
                  </h3>
                  <span className="text-xs uppercase tracking-wider font-bold text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
                    Binnenkort
                  </span>
                </div>
                <p className="text-zinc-500 leading-relaxed">
                  Laat uw overeenkomst van opdracht analyseren en ontvang 
                  concrete verbeterpunten.
                </p>
              </div>
            </motion.div>

            {/* Coming Soon Card 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="bg-white border border-zinc-200/60 shadow-sm rounded-2xl p-8 md:p-10 h-full opacity-60" data-testid="product-card-team">
                <div className="w-14 h-14 rounded-xl bg-zinc-100 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-zinc-400" />
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="font-outfit text-xl sm:text-2xl font-medium text-zinc-900">
                    Team Scan
                  </h3>
                  <span className="text-xs uppercase tracking-wider font-bold text-zinc-400 bg-zinc-100 px-2 py-1 rounded">
                    Binnenkort
                  </span>
                </div>
                <p className="text-zinc-500 leading-relaxed">
                  Voor opdrachtgevers: scan uw hele zzp-bestand in één keer 
                  en krijg overzicht van risico's.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white border border-zinc-200/60 shadow-sm rounded-2xl p-8 md:p-16 text-center"
          >
            <h2 className="font-outfit text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-zinc-900 mb-6">
              Waarom Proofix?
            </h2>
            <p className="text-lg text-zinc-600 leading-relaxed max-w-2xl mx-auto mb-4">
              De wet DBA is ingewikkeld en verandert regelmatig. Veel zzp'ers weten niet 
              of ze goed zitten — en dat kan flink in de papieren lopen.
            </p>
            <p className="text-base text-zinc-500 leading-relaxed max-w-2xl mx-auto mb-10">
              Wij maken het simpel. Geen moeilijke taal, gewoon duidelijke antwoorden.
            </p>
            <div className="grid sm:grid-cols-3 gap-8 text-left max-w-3xl mx-auto">
              <div>
                <p className="font-outfit text-3xl font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] bg-clip-text text-transparent mb-2">5 min</p>
                <p className="text-zinc-600 text-sm">Gemiddelde doorlooptijd</p>
              </div>
              <div>
                <p className="font-outfit text-3xl font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] bg-clip-text text-transparent mb-2">12</p>
                <p className="text-zinc-600 text-sm">Gerichte vragen</p>
              </div>
              <div>
                <p className="font-outfit text-3xl font-medium bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] bg-clip-text text-transparent mb-2">Direct</p>
                <p className="text-zinc-600 text-sm">Resultaat na afronding</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
